import { CronExpressionParser } from 'cron-parser';
import {
  add,
  differenceInDays,
  differenceInHours,
  differenceInMinutes,
  Duration,
  format,
  formatDistanceToNow,
  parseISO,
} from 'date-fns';
import { fromZonedTime, toZonedTime } from 'date-fns-tz';

import { WeekDay } from '@/types';

export const DEFAULT_FORMAT_TYPE = 'MMM d, yyyy';

/**
 * Formats a UTC date string to show relative time for recent dates up to a month ago,
 * and a fixed date format for older dates.
 *
 * @param {string} utcTimestamp - The UTC date string to format.
 * @return {string} - The formatted date string.
 * @param {boolean} relativeFormatting - Whether to use relative formatting.
 */
export function formatDate(
  utcTimestamp: string,
  formatType = DEFAULT_FORMAT_TYPE,
  relativeFormatting = true
) {
  // Check if the input is a valid UTC timestamp
  if (
    !utcTimestamp?.match(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d{1,3})?Z$/)
  ) {
    // check for YYYY-MM-DD format and return a nice format
    if (utcTimestamp?.match(/^\d{4}-\d{2}-\d{2}$/)) {
      return format(parseISO(utcTimestamp), formatType);
    } else {
      return utcTimestamp; // Return the input as is
    }
  }

  // Convert the UTC string to a Date object
  const date = parseISO(utcTimestamp);

  // Get the current datetime
  const currentDate = new Date();

  // If the date is more recent than 30 days ago, use relative formatting
  if (relativeFormatting && differenceInDays(currentDate, date) < 30) {
    if (differenceInDays(currentDate, date) < 1) {
      const hoursAgo = differenceInHours(currentDate, date);

      if (hoursAgo === 0) {
        const minutesAgo = differenceInMinutes(currentDate, date);

        if (minutesAgo === 0) {
          return 'now';
        }

        const unit = minutesAgo === 1 ? 'minute' : 'minutes';
        return `${minutesAgo} ${unit} ago`;
      }
      const unit = hoursAgo === 1 ? 'hour' : 'hours';
      return `${hoursAgo} ${unit} ago`;
    }
    return formatDistanceToNow(date, { addSuffix: true });
  } else {
    // Otherwise, use a fixed date format
    return format(date, formatType);
  }
}

export function sToMs(seconds: number) {
  return seconds * 1000;
}

export function mToMs(minutes: number) {
  return sToMs(minutes * 60);
}

export function sToM(seconds: number) {
  return Math.floor(seconds / 60);
}

export function msToM(ms: number) {
  return Math.floor(ms / 60000);
}

function formatRelative(date: number) {
  const now = Date.now();
  // Convert both times to UTC before calculating difference
  const utcDate = new Date(date).getTime();
  const utcNow = new Date(now).getTime();
  const minuteDiff = differenceInMinutes(utcDate, utcNow);
  const hours = Math.floor(minuteDiff / 60);
  const minutes = minuteDiff % 60;

  return { hours, minutes };
}

function getCronTimeStamp(cron: string = '') {
  const interval = CronExpressionParser.parse(cron, {
    tz: 'UTC', // Use tz: 'UTC' instead of utc: true for v5.x compatibility
  });
  const ts = interval.next().getTime();

  return ts;
}

export function getCronRelativeTime(cron: string = '') {
  const ts = getCronTimeStamp(cron);
  return formatRelative(ts);
}

export function getCurrentTimeZone() {
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const offset = new Intl.DateTimeFormat('en-US', {
    timeZone: tz,
    timeZoneName: 'longOffset',
  })
    .formatToParts(new Date())
    .find(part => part.type === 'timeZoneName')?.value;
  return {
    tz,
    offset,
  };
}

export function padTime(time: number | undefined = 0) {
  return Number(time).toString().padStart(2, '0');
}

// time: HH:MM
// tz: timezone
export function convertToUTC(time: string, tz: string) {
  // Get current date to handle DST correctly
  const now = new Date();
  const [hours, minutes] = time.split(':').map(Number);

  // Create date object for today with the specified time
  const localDate = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate(),
    hours,
    minutes
  );

  const utcDate = fromZonedTime(localDate, tz);
  const utcDateObj = new Date(utcDate.toISOString());
  const utcTime = `${padTime(utcDateObj.getUTCHours())}:${padTime(utcDateObj.getUTCMinutes())}`;

  return {
    utcTime,
    diff: utcDateObj.getUTCDate() - now.getDate(),
  };
}

export function convertUTCToTimezone(utcTime: string, tz: string) {
  const now = new Date();
  const [hours, minutes] = utcTime.split(':').map(Number);

  // Create UTC date object
  const utcDate = new Date(
    Date.UTC(now.getFullYear(), now.getMonth(), now.getDate(), hours, minutes)
  );
  const localDate = toZonedTime(utcDate, tz);
  const localTime = `${padTime(localDate.getHours())}:${padTime(localDate.getMinutes())}`;

  return {
    localTime,
    diff: localDate.getDate() - now.getDate(),
  };
}

export const getRecentTimestamps = () => {
  const oneHour = 60 * 60 * 1000;
  const oneDay = 24 * oneHour;

  // Round to the nearest hour to prevent query key changes on every render.
  const nowRounded = Math.floor(Date.now() / oneHour) * oneHour;

  return {
    '24h': new Date(nowRounded - oneDay).toISOString(),
    '48h': new Date(nowRounded - 2 * oneDay).toISOString(),
    '5days': new Date(nowRounded - 5 * oneDay).toISOString(),
    '7days': new Date(nowRounded - 7 * oneDay).toISOString(),
    '20days': new Date(nowRounded - 20 * oneDay).toISOString(),
  };
};

export const convertDayToUTC = (day: WeekDay, timezoneDiffFromUTC: number) => {
  const dayInUtc = day + timezoneDiffFromUTC;
  if (dayInUtc > 6) {
    return 0;
  }
  if (dayInUtc < 0) {
    return 6;
  }
  return dayInUtc;
};

export const getFutureDateInISO = (duration: Duration) => {
  return add(new Date(), duration).toISOString();
};
